import { QueryKey, useQuery } from '@tanstack/react-query';
import { GetUserQueryResponse, User } from 'app/data/types/user';
import { useApi } from 'app/shared/contexts/api/api.context';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useEffect } from 'react';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';

export const USER_QUERY_KEY = 'user';

export const getUseUserQueryKey = (): QueryKey => [USER_QUERY_KEY];

export const useUserQuery = (sdkUser?: User | null): GetUserQueryResponse => {
  const { addError, hideErrors } = useErrors();
  const { getAuthHeaders, getDefaultHeaders, authToken, apiUrl } = useApi();
  const getUserRequest = async (): Promise<User | null> => {
    const headers = await getAuthHeaders();
    const requestHeaders = { ...headers, ...getDefaultHeaders() };

    const response = await apiRequest<User, void, void>({
      url: `${apiUrl}/api/users/current`,
      method: HttpMethod.GET,
      headers: requestHeaders,
    });

    if (response && !response.isEmpty()) {
      return response.data;
    }

    return null;
  };

  const { data, error, isLoading, isSuccess } = useQuery<User | null, Error>({
    queryKey: getUseUserQueryKey(),
    queryFn: getUserRequest,
    enabled: !!authToken && !sdkUser,
    retry: false,
  });

  // Functional (non-blocking) error: the widgets don't depend on the user, so a failed
  // user fetch must not gate render or trip the error page.
  useEffect(() => {
    if (error) {
      addError(USER_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideErrors(USER_QUERY_KEY);
    }
  }, [error, addError, hideErrors]);

  return {
    user: sdkUser ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
